import { Dialog, DialogBackdrop, DialogPanel, DialogTitle } from "@headlessui/react";
import axios from "axios";
import React from "react";
import { useDispatch, useSelector } from "react-redux";
import { classNames } from "../../helpers";
import * as dashboardActions from "../../redux/reducers/dashboardReducer";
import { userState } from "../../redux/reducers/userReducer";
import axiosInstance from "../../utils/axiosInstance";
import { getUrlForBookings, getUrlForClasses, getUrlForCronServer } from "../../utils/confighelpers";
import { classTypes } from "../../utils/constants";
import "../../styles/onboarding.css";

/**
 * Side drawer to create or edit a class (catalogue item only — no scheduling).
 * Scheduling happens separately (My Schedule / class detail).
 *
 * Saves through the existing endpoints:
 *   POST schools/setClass        (create)
 *   PUT  schools/updateSetClass  (edit / delete)
 * The single-class price lives in eventPricingList[0] ("Single Session"), the same
 * shape the old wizard wrote, so bookings, schedules and the member apps keep working.
 */

export const CANCELLATION_OPTIONS = [
  { key: "1hr", label: "1 hour", text: "1 hour", minutes: 60 },
  { key: "24hrs", label: "24 hours", text: "24 hours", minutes: 1440 },
  { key: "48hrs", label: "48 hours", text: "48 hours", minutes: 2880 },
  { key: "1 week", label: "1 week", text: "1 week", minutes: 10080 },
];
const QUICK_DURATIONS = [30, 45, 60, 75, 90, 120];
export const MIN_DURATION = 5; // minutes
export const MAX_DURATION = 480; // 8 hours, same upper limit as the old wizard
const PRESET_TYPES = classTypes.filter((t) => t.value).map((t) => t.value);
const MAX_PHOTO_MB = 8;
// Single-class booking is dropped: classes are booked with passes / memberships (trial = free sign-up credit).
// The booking system still expects a "Single Session" price on every class, so new classes get this
// placeholder price (and single booking switched off). To be removed completely later.
export const DEFAULT_SINGLE_PRICE = 10;
// Hidden for now: clubs don't use "invite code only" or "pay at venue".
// Existing values on a class are kept untouched when it is edited. Set to true to bring the toggles back.
export const SHOW_INVITE_AND_PAY_AT_VENUE = false;

export const formatDuration = (minutes) => {
  const m = Number(minutes) || 0;
  const h = Math.floor(m / 60);
  const r = m % 60;
  if (!h) return `${r} min`;
  return r ? `${h} hr ${r} min` : `${h} hr`;
};

export const formatPrice = (amount) => {
  const n = Number(amount);
  if (!Number.isFinite(n) || n <= 0) return "Free";
  return `€${n % 1 === 0 ? n : n.toFixed(2)}`;
};

/** Reads the single-class price/capacity from a class document. */
export const getSingleSessionPricing = (classDoc = {}) => {
  const list = Array.isArray(classDoc.eventPricingList) ? classDoc.eventPricingList : [];
  const single = list.find((p) => (p?.ticketType || "").toLowerCase() === "single session") || list[0];
  return {
    amount: single ? single.amount : classDoc.cost,
    capacity: single?.capacity || classDoc.capacity,
  };
};

const emptyForm = {
  photo: "",
  className: "",
  classType: "",
  customType: "",
  instructorName: "",
  description: "",
  duration: 60,
  allowSingle: false,
  price: String(DEFAULT_SINGLE_PRICE),
  capacity: "20",
  freeCancellation: true,
  cancellationPeriod: "24hrs",
  inviteCodeOnlyBooking: false,
  paymentAtVenue: false,
};

const formFromClass = (c, ownerName = "") => {
  if (!c) return { ...emptyForm, instructorName: ownerName };
  const { amount, capacity } = getSingleSessionPricing(c);
  const minutes = c.classCancelationPolicy?.minutes;
  const period = CANCELLATION_OPTIONS.find((o) => o.minutes === minutes)?.key || "24hrs";
  return {
    photo: c.classPhoto || "",
    className: c.className || "",
    classType: c.classType || "", // a type outside the preset list is added to the dropdown options
    customType: "",
    instructorName: c.instructorName || ownerName,
    description: c.description || "",
    duration: Number(c.duration) || 60,
    allowSingle: c.allowSingleSessionPurchase !== false,
    price: amount !== undefined && amount !== null && amount !== "" ? String(amount) : String(DEFAULT_SINGLE_PRICE),
    capacity: capacity ? String(capacity) : "20",
    freeCancellation: !!c.classCancelationPolicy?.isEnabled,
    cancellationPeriod: period,
    inviteCodeOnlyBooking: !!c.isPrivate,
    paymentAtVenue: !!c.paymentAtVenue,
  };
};

function Icon({ d, className = "h-5 w-5" }) {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className={className} aria-hidden="true">
      {d}
    </svg>
  );
}

function Toggle({ checked, onChange, disabled, label }) {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={checked}
      aria-label={label}
      disabled={disabled}
      onClick={() => !disabled && onChange(!checked)}
      className={classNames(
        "relative inline-flex h-7 w-12 shrink-0 items-center rounded-full transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-indigo-bz focus-visible:ring-offset-2",
        checked ? "bg-indigo-bz" : "bg-gray-300",
        disabled ? "cursor-not-allowed opacity-50" : "cursor-pointer"
      )}
    >
      <span className={classNames("inline-block h-5 w-5 transform rounded-full bg-white shadow transition-transform", checked ? "translate-x-6" : "translate-x-1")} />
    </button>
  );
}

function Chip({ active, onClick, children }) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-pressed={active}
      className={classNames(
        "rounded-full border px-3.5 py-1.5 text-sm font-bold transition-colors",
        active ? "border-indigo-bz bg-[#F3E9FF] text-indigo-bz" : "border-gray-200 bg-white text-gray-700 hover:border-gray-300 hover:bg-gray-50"
      )}
    >
      {children}
    </button>
  );
}

function FieldLabel({ htmlFor, children, optional }) {
  return (
    <label htmlFor={htmlFor} className="mb-1.5 flex items-center gap-2 text-sm font-extrabold text-gray-900">
      {children}
      {optional && <span className="text-xs font-semibold text-gray-400">Optional</span>}
    </label>
  );
}

function FieldError({ children }) {
  if (!children) return null;
  return (
    <p className="mt-1.5 text-sm font-semibold text-red-600" role="alert">
      {children}
    </p>
  );
}

const inputClass = (hasError) =>
  classNames(
    "block w-full rounded-2xl border bg-white px-4 py-3 text-base text-gray-900 placeholder:text-gray-400 outline-none transition focus:ring-4",
    hasError ? "border-red-300 focus:border-red-500 focus:ring-red-100" : "border-gray-200 focus:border-indigo-bz focus:ring-[#F3E9FF]"
  );

function PolicyRow({ icon, iconBg, iconColor, title, text, checked, onChange, disabled, children }) {
  return (
    <div className={classNames("rounded-2xl border p-4 transition-colors", checked ? "border-[#E3D0F7] bg-[#FBF8FE]" : "border-gray-200 bg-white")}>
      <div className="flex items-start gap-3">
        <span className={classNames("flex h-10 w-10 shrink-0 items-center justify-center rounded-xl", iconBg, iconColor)}>{icon}</span>
        <div className="min-w-0 flex-1">
          <p className="text-sm font-extrabold text-gray-900">{title}</p>
          <p className="mt-0.5 text-sm text-gray-600">{text}</p>
        </div>
        <Toggle checked={checked} onChange={onChange} disabled={disabled} label={title} />
      </div>
      {children}
    </div>
  );
}

export default function ClassDrawer({ open, onClose, classData = null, onSaved, onDeleted, onSchedule }) {
  const dispatch = useDispatch();
  const userReduxState = useSelector(userState);
  const isEdit = !!classData?._id;
  const ownerName = (userReduxState.userData?.fullName || "").trim();
  const [form, setForm] = React.useState(() => formFromClass(classData, ownerName));
  const [errors, setErrors] = React.useState({});
  const [saving, setSaving] = React.useState(false);
  const [uploading, setUploading] = React.useState(false);
  const [dragOver, setDragOver] = React.useState(false);
  const [customDuration, setCustomDuration] = React.useState(false);
  const [customH, setCustomH] = React.useState("");
  const [customM, setCustomM] = React.useState("");
  const [payAtVenueEnabled, setPayAtVenueEnabled] = React.useState(false);
  const [confirmDelete, setConfirmDelete] = React.useState(false);
  const [deleting, setDeleting] = React.useState(false);
  const [created, setCreated] = React.useState(null);
  const fileInputRef = React.useRef(null);
  const scrollRef = React.useRef(null);

  // Reset whenever the drawer opens for a (different) class.
  React.useEffect(() => {
    if (!open) return;
    const next = formFromClass(classData, ownerName);
    setForm(next);
    setErrors({});
    setSaving(false);
    setUploading(false);
    setConfirmDelete(false);
    setDeleting(false);
    setCreated(null);
    const isCustom = !QUICK_DURATIONS.includes(Number(next.duration));
    setCustomDuration(isCustom);
    setCustomH(isCustom ? String(Math.floor(Number(next.duration) / 60)) : "");
    setCustomM(isCustom ? String(Number(next.duration) % 60) : "");
  }, [open, classData]); // eslint-disable-line react-hooks/exhaustive-deps

  React.useEffect(() => {
    if (!SHOW_INVITE_AND_PAY_AT_VENUE || !open || !userReduxState.userData?._id) return;
    let cancelled = false;
    (async () => {
      try {
        const response = await axiosInstance({
          method: "get",
          url: `${getUrlForBookings()}payments/getAvailablePaymentMethods?teacherID=${userReduxState.userData._id}&paymentMethodsFor=class&bookingFor=class`,
        });
        if (!cancelled && response?.data?.statusCode === 200) {
          setPayAtVenueEnabled(!!response.data.data?.payAtVenue?.enable);
        }
      } catch (e) {
        // Not critical: the toggle simply stays disabled.
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [open, userReduxState.userData?._id]);

  const setField = (name, value) => {
    setForm((prev) => ({ ...prev, [name]: value }));
    if (errors[name]) setErrors((prev) => ({ ...prev, [name]: "" }));
  };

  const uploadPhoto = async (file) => {
    if (!file) return;
    if (!/^image\/(jpe?g|png|webp|gif)$/i.test(file.type)) {
      setErrors((prev) => ({ ...prev, photo: "Use a JPG, PNG or WebP image." }));
      return;
    }
    if (file.size > MAX_PHOTO_MB * 1024 * 1024) {
      setErrors((prev) => ({ ...prev, photo: `Images up to ${MAX_PHOTO_MB} MB, please.` }));
      return;
    }
    const data = new FormData();
    data.append("uploadingFor", "TestClass");
    data.append("file", file);
    setUploading(true);
    try {
      const response = await axios.request({
        method: "post",
        maxBodyLength: Infinity,
        url: `${getUrlForCronServer()}media-upload`,
        headers: { "Content-Type": "multipart/form-data" },
        data,
      });
      if (response?.data?.url) {
        setField("photo", response.data.url);
      } else {
        setErrors((prev) => ({ ...prev, photo: "Upload failed. Try another image." }));
      }
    } catch (error) {
      setErrors((prev) => ({ ...prev, photo: error?.response?.data?.error || "Upload failed. Try again." }));
    } finally {
      setUploading(false);
    }
  };

  const resolvedType = form.classType === "__other" ? form.customType.trim() : form.classType;

  const validate = () => {
    const e = {};
    if (!form.photo) e.photo = "Add a photo so members recognise the class.";
    if (!form.className.trim()) e.className = "Give your class a name.";
    else if (form.className.trim().length < 3) e.className = "Use at least 3 characters.";
    if (!resolvedType) e.classType = form.classType === "__other" ? "Type the class type." : "Pick a class type.";
    if (!form.instructorName.trim()) e.instructorName = "Who teaches this class?";
    const dur = Number(form.duration);
    if (!dur) e.duration = customDuration ? "Enter hours and/or minutes." : "Pick a duration.";
    else if (dur < MIN_DURATION) e.duration = `At least ${MIN_DURATION} minutes.`;
    else if (dur > MAX_DURATION) e.duration = "Classes can be up to 8 hours.";
    const cap = Number(form.capacity);
    if (!Number.isInteger(cap) || cap < 1) e.capacity = "At least 1 spot.";
    else if (cap > 1000) e.capacity = "That's a lot — max 1000.";
    if (form.freeCancellation && !form.cancellationPeriod) e.cancellationPeriod = "Pick a cancellation window.";
    return e;
  };

  const buildPayload = () => {
    // No price field any more: new classes get DEFAULT_SINGLE_PRICE, edits keep the class's existing price.
    const rawPrice = Number(String(form.price).replace(",", "."));
    const price = Number.isFinite(rawPrice) && rawPrice >= 0 ? Math.round(rawPrice * 100) / 100 : DEFAULT_SINGLE_PRICE;
    const capacity = Number(form.capacity);
    const existingList = Array.isArray(classData?.eventPricingList) ? classData.eventPricingList : [];
    const singleIdx = existingList.findIndex((p) => (p?.ticketType || "").toLowerCase() === "single session");
    const idx = singleIdx >= 0 ? singleIdx : existingList.length ? 0 : -1;
    const single = {
      ...(idx >= 0 ? existingList[idx] : { ticketType: "Single Session", absorbServiceFee: false }),
      amount: String(price),
      capacity: String(capacity),
    };
    const eventPricingList = idx >= 0 ? existingList.map((p, i) => (i === idx ? single : p)) : [single];
    const cancel = CANCELLATION_OPTIONS.find((o) => o.key === form.cancellationPeriod);

    return {
      isPackage: false,
      classType: resolvedType,
      allowSingleSessionPurchase: !!form.allowSingle,
      instructorName: form.instructorName.trim(),
      className: form.className.trim(),
      description: form.description.trim(),
      cost: 0,
      tagLines: classData?.tagLines || "",
      capacity,
      duration: Number(form.duration),
      whatToCarry: classData?.whatToCarry || "Nothing specified",
      classPhoto: form.photo,
      isPrivate: SHOW_INVITE_AND_PAY_AT_VENUE ? form.inviteCodeOnlyBooking : !!classData?.isPrivate,
      classCancelationPolicy: form.freeCancellation && cancel ? { isEnabled: true, text: cancel.text, minutes: cancel.minutes } : { isEnabled: false },
      paymentAtVenue: !SHOW_INVITE_AND_PAY_AT_VENUE
        ? !!classData?.paymentAtVenue
        : payAtVenueEnabled ? form.paymentAtVenue : !!classData?.paymentAtVenue && form.paymentAtVenue,
      isEvent: false,
      eventPricingList,
      multiSessionPassList: Array.isArray(classData?.multiSessionPassList) ? classData.multiSessionPassList : [],
    };
  };

  const handleSave = async () => {
    const e = validate();
    setErrors(e);
    if (Object.keys(e).length) {
      scrollRef.current?.scrollTo({ top: 0, behavior: "smooth" });
      return;
    }
    const body = buildPayload();
    setSaving(true);
    try {
      const request = await axiosInstance(
        isEdit
          ? { method: "put", url: `${getUrlForClasses()}updateSetClass`, data: { ...body, classId: classData._id } }
          : {
              method: "post",
              url: `${getUrlForClasses()}setClass`,
              data: { ...body, dataFor: userReduxState.businessType || "class", isPublic: true, isClassPublic: true },
            }
      );
      const res = request?.data;
      if (res?.statusCode === 200) {
        if (!isEdit) {
          dispatch(dashboardActions.setNewClassRes(res.data));
          dispatch(dashboardActions.setClassToSchedule(res.data));
          setCreated(res.data);
          scrollRef.current?.scrollTo({ top: 0 });
        }
        if (onSaved) onSaved(res.data, { isEdit });
        if (isEdit && onClose) onClose();
      } else {
        const msg = typeof res?.message === "string" ? res.message : "Something went wrong. Please try again.";
        if (/already exist/i.test(msg)) setErrors({ className: "You already have a class with this name." });
        else setErrors({ form: msg });
      }
    } catch (err) {
      setErrors({ form: "Couldn't reach the server. Check your connection and try again." });
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    setDeleting(true);
    try {
      const request = await axiosInstance({
        method: "put",
        url: `${getUrlForClasses()}updateSetClass`,
        data: {
          classId: classData._id,
          isDeleted: true,
          eventPricingList: Array.isArray(classData.eventPricingList) ? classData.eventPricingList : [],
          multiSessionPassList: Array.isArray(classData.multiSessionPassList) ? classData.multiSessionPassList : [],
          isEvent: false,
        },
      });
      const res = request?.data;
      if (res?.statusCode === 200) {
        if (onDeleted) onDeleted(classData);
        if (onClose) onClose();
      } else {
        setErrors({ form: typeof res?.message === "string" ? res.message : "Couldn't delete this class." });
        setConfirmDelete(false);
      }
    } catch (err) {
      setErrors({ form: "Couldn't reach the server. Try again." });
    } finally {
      setDeleting(false);
    }
  };

  const busy = saving || uploading || deleting;
  const typeOptions = form.classType && form.classType !== "__other" && !PRESET_TYPES.includes(form.classType) ? [...PRESET_TYPES, form.classType] : PRESET_TYPES;
  const updateCustomDuration = (h, m) => {
    setCustomH(h);
    setCustomM(m);
    setField("duration", (Number(h) || 0) * 60 + (Number(m) || 0));
  };

  return (
    <Dialog open={open} onClose={() => !busy && onClose && onClose()} className="relative z-50">
      <DialogBackdrop transition className="fixed inset-0 bg-[#1d1320]/50 backdrop-blur-[2px] transition-opacity duration-300 ease-out data-[closed]:opacity-0" />
      <div className="fixed inset-0 overflow-hidden">
        <div className="pointer-events-none fixed inset-y-0 right-0 flex max-w-full sm:pl-10">
          <DialogPanel
            transition
            className="pointer-events-auto flex h-full w-screen max-w-xl transform flex-col bg-[#F7F4F8] shadow-2xl transition duration-300 ease-out data-[closed]:translate-x-full sm:rounded-l-3xl"
          >
            {/* Header */}
            <div className="flex items-start justify-between gap-4 border-b border-gray-200 bg-white px-5 py-4 sm:rounded-tl-3xl sm:px-7 sm:py-5">
              <div className="min-w-0">
                <p className="text-xs font-extrabold tracking-wide text-portland-orange">{isEdit ? "EDIT CLASS" : "NEW CLASS"}</p>
                <DialogTitle className="mt-0.5 truncate text-xl font-black tracking-tight text-gray-900 sm:text-2xl">
                  {created ? "Class created" : isEdit ? classData.className : "Add a class"}
                </DialogTitle>
                {!created && (
                  <p className="mt-1 text-sm text-gray-600">
                    {isEdit ? "Changes apply to all upcoming sessions of this class." : "What you offer, its price and rules. You'll schedule it next."}
                  </p>
                )}
              </div>
              <button
                type="button"
                onClick={() => !busy && onClose && onClose()}
                className="-mr-1 flex h-10 w-10 shrink-0 items-center justify-center rounded-xl text-gray-500 hover:bg-gray-100 hover:text-gray-900"
                aria-label="Close"
              >
                <Icon d={<path d="M18 6 6 18M6 6l12 12" />} />
              </button>
            </div>

            {created ? (
              <div className="flex flex-1 flex-col items-center justify-center overflow-y-auto px-6 py-10 text-center">
                <div className="fc-pop relative mb-6 flex h-20 w-20 items-center justify-center rounded-full bg-bz-status-green shadow-[0_16px_36px_rgba(70,140,17,0.3)]">
                  <Icon className="h-10 w-10 text-white" d={<path d="M5 12l5 5L20 7" />} />
                </div>
                <h3 className="text-2xl font-black tracking-tight text-gray-900">{created.className} is ready</h3>
                <p className="mt-2 max-w-sm text-base text-gray-600">
                  It's saved in My Classes. Put it on your timetable so members can book it.
                </p>
                <div className="mt-6 w-full max-w-sm overflow-hidden rounded-3xl border border-gray-200 bg-white text-left shadow-[0_20px_50px_rgba(72,27,58,0.08)]">
                  {created.classPhoto && <img src={created.classPhoto} alt="" className="aspect-[16/9] w-full object-cover" />}
                  <div className="p-4">
                    <p className="text-base font-black text-gray-900">{created.className}</p>
                    <p className="mt-0.5 text-sm text-gray-600">
                      {created.classType}{created.instructorName ? ` · with ${created.instructorName}` : ""} · {formatDuration(created.duration)}
                    </p>
                  </div>
                </div>
                <div className="mt-8 flex w-full max-w-sm flex-col gap-3">
                  {onSchedule && (
                    <button
                      type="button"
                      onClick={() => onSchedule(created)}
                      className="w-full rounded-2xl bg-brown-coffee py-3.5 text-base font-extrabold text-white hover:bg-light-brown-coffee"
                    >
                      Add to my timetable
                    </button>
                  )}
                  <button
                    type="button"
                    onClick={() => {
                      setCreated(null);
                      setForm({ ...emptyForm, instructorName: ownerName });
                      setErrors({});
                      setCustomDuration(false);
                      setCustomH("");
                      setCustomM("");
                    }}
                    className="w-full rounded-2xl border border-gray-200 bg-white py-3.5 text-base font-extrabold text-gray-900 hover:bg-gray-50"
                  >
                    Add another class
                  </button>
                  <button type="button" onClick={onClose} className="py-2 text-sm font-bold text-gray-600 hover:text-gray-900">
                    Done for now
                  </button>
                </div>
              </div>
            ) : (
              <>
                <div ref={scrollRef} className="flex-1 overflow-y-auto px-5 py-6 sm:px-7">
                  {errors.form && (
                    <div className="mb-5 rounded-2xl border border-red-200 bg-red-50 px-4 py-3 text-sm font-semibold text-red-700" role="alert">
                      {errors.form}
                    </div>
                  )}

                  {/* Photo */}
                  <section className="rounded-3xl border border-gray-200 bg-white p-4 shadow-[0_10px_30px_rgba(72,27,58,0.05)] sm:p-5">
                    <FieldLabel>Class photo</FieldLabel>
                    <div
                      onDragOver={(ev) => {
                        ev.preventDefault();
                        setDragOver(true);
                      }}
                      onDragLeave={() => setDragOver(false)}
                      onDrop={(ev) => {
                        ev.preventDefault();
                        setDragOver(false);
                        uploadPhoto(ev.dataTransfer.files?.[0]);
                      }}
                      className={classNames(
                        "group relative flex aspect-[16/9] w-full items-center justify-center overflow-hidden rounded-2xl border-2 border-dashed transition-colors",
                        errors.photo ? "border-red-300 bg-red-50" : dragOver ? "border-indigo-bz bg-[#F3E9FF]" : form.photo ? "border-transparent" : "border-gray-200 bg-[#FBF8FE]"
                      )}
                    >
                      {form.photo && <img src={form.photo} alt="Class" className="absolute inset-0 h-full w-full object-cover" />}
                      {uploading ? (
                        <div className="relative flex flex-col items-center gap-2 rounded-2xl bg-white/90 px-5 py-4">
                          <span className="h-7 w-7 animate-spin rounded-full border-[3px] border-[#E3D0F7] border-t-indigo-bz" />
                          <span className="text-sm font-bold text-gray-700">Uploading…</span>
                        </div>
                      ) : form.photo ? (
                        <div className="absolute bottom-3 right-3 flex gap-2">
                          <button
                            type="button"
                            onClick={() => fileInputRef.current?.click()}
                            className="rounded-xl bg-white/95 px-3 py-2 text-sm font-extrabold text-gray-900 shadow hover:bg-white"
                          >
                            Replace
                          </button>
                        </div>
                      ) : (
                        <button type="button" onClick={() => fileInputRef.current?.click()} className="flex flex-col items-center gap-2 px-6 text-center">
                          <span className="flex h-12 w-12 items-center justify-center rounded-2xl bg-[#F3E9FF] text-indigo-bz">
                            <Icon d={<><rect x="3" y="5" width="18" height="14" rx="3" /><circle cx="9" cy="10" r="2" /><path d="m21 15-4.5-4.5L7 19" /></>} />
                          </span>
                          <span className="text-sm font-extrabold text-gray-900">Drop a photo or click to upload</span>
                          <span className="text-xs text-gray-500">Landscape works best · JPG, PNG or WebP · up to {MAX_PHOTO_MB} MB</span>
                        </button>
                      )}
                    </div>
                    <input
                      ref={fileInputRef}
                      type="file"
                      accept="image/jpeg,image/png,image/webp,image/gif"
                      className="hidden"
                      onChange={(ev) => {
                        uploadPhoto(ev.target.files?.[0]);
                        ev.target.value = "";
                      }}
                    />
                    <FieldError>{errors.photo}</FieldError>
                  </section>

                  {/* Basics */}
                  <section className="mt-5 rounded-3xl border border-gray-200 bg-white p-4 shadow-[0_10px_30px_rgba(72,27,58,0.05)] sm:p-5">
                    <h3 className="text-base font-black text-gray-900">About the class</h3>
                    <div className="mt-4 flex flex-col gap-5">
                      <div>
                        <FieldLabel htmlFor="fc-class-type">Class type</FieldLabel>
                        <div className="relative">
                          <select
                            id="fc-class-type"
                            className={classNames(inputClass(errors.classType), "appearance-none pr-11", form.classType ? "" : "text-gray-400")}
                            value={form.classType}
                            onChange={(ev) => setField("classType", ev.target.value)}
                          >
                            <option value="" disabled>
                              Select a class type
                            </option>
                            {typeOptions.map((t) => (
                              <option key={t} value={t} className="text-gray-900">
                                {t}
                              </option>
                            ))}
                            <option value="__other" className="text-gray-900">
                              Other (type your own)…
                            </option>
                          </select>
                          <span className="pointer-events-none absolute inset-y-0 right-4 flex items-center text-gray-500">
                            <Icon className="h-5 w-5" d={<path d="m6 9 6 6 6-6" />} />
                          </span>
                        </div>
                        {form.classType === "__other" && (
                          <input
                            className={classNames(inputClass(errors.classType), "mt-3")}
                            placeholder="e.g. Pilates, Boxing, Zumba"
                            aria-label="Custom class type"
                            maxLength={40}
                            value={form.customType}
                            autoFocus
                            onChange={(ev) => {
                              setForm((prev) => ({ ...prev, customType: ev.target.value }));
                              if (errors.classType) setErrors((prev) => ({ ...prev, classType: "" }));
                            }}
                          />
                        )}
                        <FieldError>{errors.classType}</FieldError>
                      </div>

                      <div>
                        <FieldLabel htmlFor="fc-class-name">Class name</FieldLabel>
                        <input
                          id="fc-class-name"
                          className={inputClass(errors.className)}
                          placeholder="e.g. Yoga Flow"
                          maxLength={60}
                          value={form.className}
                          onChange={(ev) => setField("className", ev.target.value)}
                        />
                        <FieldError>{errors.className}</FieldError>
                      </div>

                      <div>
                        <FieldLabel htmlFor="fc-class-instructor">Default coach</FieldLabel>
                        <div className="relative">
                          <span className="pointer-events-none absolute inset-y-0 left-4 flex items-center text-gray-400">
                            <Icon className="h-5 w-5" d={<><circle cx="12" cy="8" r="4" /><path d="M4 21a8 8 0 0 1 16 0" /></>} />
                          </span>
                          <input
                            id="fc-class-instructor"
                            className={classNames(inputClass(errors.instructorName), "pl-11")}
                            placeholder="Who teaches this class?"
                            maxLength={80}
                            value={form.instructorName}
                            onChange={(ev) => setField("instructorName", ev.target.value)}
                          />
                        </div>
                        <p className="mt-1.5 text-xs text-gray-500">Filled in when you add sessions, and you can pick someone else there. Changing it here only affects sessions you add later. For a substitute, use Change coach on that session in My Schedule.</p>
                        <FieldError>{errors.instructorName}</FieldError>
                      </div>

                      <div>
                        <FieldLabel htmlFor="fc-class-cap">Spots per session</FieldLabel>
                        <div className="relative">
                          <span className="pointer-events-none absolute inset-y-0 left-4 flex items-center text-gray-400">
                            <Icon className="h-5 w-5" d={<><circle cx="9" cy="8" r="3.5" /><path d="M2.5 20a6.5 6.5 0 0 1 13 0" /><path d="M16 4.5a3.5 3.5 0 0 1 0 7" /><path d="M18.5 20a6.5 6.5 0 0 0-2.5-5.1" /></>} />
                          </span>
                          <input
                            id="fc-class-cap"
                            inputMode="numeric"
                            className={classNames(inputClass(errors.capacity), "pl-11")}
                            placeholder="20"
                            maxLength={4}
                            value={form.capacity}
                            onChange={(ev) => setField("capacity", ev.target.value.replace(/[^0-9]/g, ""))}
                          />
                        </div>
                        <p className="mt-1.5 text-xs text-gray-500">Max members who can book one session. Members book with a credit pass or subscription.</p>
                        <FieldError>{errors.capacity}</FieldError>
                      </div>

                      <div>
                        <FieldLabel>Duration</FieldLabel>
                        <div className="flex flex-wrap gap-2">
                          {QUICK_DURATIONS.map((m) => (
                            <Chip
                              key={m}
                              active={!customDuration && Number(form.duration) === m}
                              onClick={() => {
                                setCustomDuration(false);
                                setField("duration", m);
                              }}
                            >
                              {formatDuration(m)}
                            </Chip>
                          ))}
                          <Chip
                            active={customDuration}
                            onClick={() => {
                              if (customDuration) return;
                              const cur = Number(form.duration) || 0;
                              setCustomDuration(true);
                              updateCustomDuration(cur ? String(Math.floor(cur / 60)) : "", cur ? String(cur % 60) : "");
                            }}
                          >
                            Custom
                          </Chip>
                        </div>
                        {customDuration && (
                          <div className="mt-3 flex items-end gap-3">
                            <label className="flex-1">
                              <span className="mb-1 block text-xs font-bold text-gray-500">Hours</span>
                              <input
                                inputMode="numeric"
                                aria-label="Duration hours"
                                className={inputClass(errors.duration)}
                                placeholder="0"
                                maxLength={1}
                                value={customH}
                                onChange={(ev) => updateCustomDuration(ev.target.value.replace(/[^0-9]/g, "").slice(0, 1), customM)}
                              />
                            </label>
                            <label className="flex-1">
                              <span className="mb-1 block text-xs font-bold text-gray-500">Minutes</span>
                              <input
                                inputMode="numeric"
                                aria-label="Duration minutes"
                                className={inputClass(errors.duration)}
                                placeholder="50"
                                maxLength={2}
                                value={customM}
                                onChange={(ev) => {
                                  const v = ev.target.value.replace(/[^0-9]/g, "").slice(0, 2);
                                  updateCustomDuration(customH, v && Number(v) > 59 ? "59" : v);
                                }}
                              />
                            </label>
                            <span className="mb-3 min-w-20 text-sm font-bold text-gray-700">
                              {Number(form.duration) > 0 ? `= ${formatDuration(form.duration)}` : ""}
                            </span>
                          </div>
                        )}
                        <FieldError>{errors.duration}</FieldError>
                      </div>

                      <div>
                        <FieldLabel htmlFor="fc-class-desc" optional>
                          Description
                        </FieldLabel>
                        <textarea
                          id="fc-class-desc"
                          rows={3}
                          maxLength={1000}
                          className={classNames(inputClass(false), "resize-y")}
                          placeholder="What will members do and feel? Level, what to bring…"
                          value={form.description}
                          onChange={(ev) => setField("description", ev.target.value)}
                        />
                      </div>
                    </div>
                  </section>

                  {/* Policies */}
                  <section className="mt-5 rounded-3xl border border-gray-200 bg-white p-4 shadow-[0_10px_30px_rgba(72,27,58,0.05)] sm:p-5">
                    <h3 className="text-base font-black text-gray-900">Booking policies</h3>
                    <div className="mt-4 flex flex-col gap-3">
                      <PolicyRow
                        icon={<Icon d={<><circle cx="12" cy="12" r="9" /><path d="M12 7v5l3 2" /></>} />}
                        iconBg="bg-[#E8F7EE]"
                        iconColor="text-bz-status-green"
                        title="Free cancellation"
                        text="Members can cancel and get their credit or money back."
                        checked={form.freeCancellation}
                        onChange={(v) => setField("freeCancellation", v)}
                      >
                        {form.freeCancellation && (
                          <div className="mt-3 pl-[52px]">
                            <p className="mb-2 text-xs font-bold text-gray-500">Up to this long before the class starts</p>
                            <div className="flex flex-wrap gap-2">
                              {CANCELLATION_OPTIONS.map((o) => (
                                <Chip key={o.key} active={form.cancellationPeriod === o.key} onClick={() => setField("cancellationPeriod", o.key)}>
                                  {o.label}
                                </Chip>
                              ))}
                            </div>
                            <FieldError>{errors.cancellationPeriod}</FieldError>
                          </div>
                        )}
                      </PolicyRow>
                      {SHOW_INVITE_AND_PAY_AT_VENUE && (<>
                      <PolicyRow
                        icon={<Icon d={<><rect x="4" y="10" width="16" height="10" rx="2" /><path d="M8 10V7a4 4 0 0 1 8 0v3" /></>} />}
                        iconBg="bg-[#F3E9FF]"
                        iconColor="text-indigo-bz"
                        title="Invite code only"
                        text="Hidden from your public page. Only members with the class code can book."
                        checked={form.inviteCodeOnlyBooking}
                        onChange={(v) => setField("inviteCodeOnlyBooking", v)}
                      >
                        {isEdit && form.inviteCodeOnlyBooking && classData?.bookingCode && (
                          <p className="mt-3 pl-[52px] text-sm text-gray-700">
                            Class code: <span className="rounded-lg bg-[#F3E9FF] px-2 py-0.5 font-mono font-bold text-indigo-bz">{classData.bookingCode}</span>
                          </p>
                        )}
                      </PolicyRow>
                      <PolicyRow
                        icon={<Icon d={<><rect x="2" y="6" width="20" height="13" rx="2" /><path d="M2 10h20M6 15h4" /></>} />}
                        iconBg="bg-[#FFE8EB]"
                        iconColor="text-portland-orange"
                        title="Pay at the venue"
                        text={payAtVenueEnabled ? "Members can reserve now and pay when they arrive." : "Turn on “pay at venue” in your payment settings to use this."}
                        checked={form.paymentAtVenue}
                        onChange={(v) => setField("paymentAtVenue", v)}
                        disabled={!payAtVenueEnabled && !form.paymentAtVenue}
                      />
                      </>)}
                    </div>
                  </section>

                  {isEdit && (
                    <section className="mt-5 mb-2 rounded-3xl border border-red-100 bg-white p-4 sm:p-5">
                      {confirmDelete ? (
                        <div className="flex flex-col gap-3">
                          <p className="text-sm font-bold text-gray-900">Delete “{classData.className}”?</p>
                          <p className="text-sm text-gray-600">Past bookings stay in your history. Classes with upcoming sessions can't be deleted — cancel those first.</p>
                          <div className="flex gap-2">
                            <button
                              type="button"
                              onClick={handleDelete}
                              disabled={deleting}
                              className="rounded-xl bg-red-600 px-4 py-2 text-sm font-extrabold text-white hover:bg-red-700 disabled:opacity-60"
                            >
                              {deleting ? "Deleting…" : "Yes, delete"}
                            </button>
                            <button type="button" onClick={() => setConfirmDelete(false)} className="rounded-xl px-4 py-2 text-sm font-bold text-gray-600 hover:bg-gray-100">
                              Keep it
                            </button>
                          </div>
                        </div>
                      ) : (
                        <button type="button" onClick={() => setConfirmDelete(true)} className="text-sm font-extrabold text-red-600 hover:text-red-700">
                          Delete this class
                        </button>
                      )}
                    </section>
                  )}
                </div>

                {/* Footer */}
                <div className="flex items-center justify-between gap-3 border-t border-gray-200 bg-white px-5 py-4 sm:rounded-bl-3xl sm:px-7">
                  <button type="button" onClick={onClose} disabled={busy} className="rounded-xl px-4 py-3 text-sm font-bold text-gray-600 hover:bg-gray-100 disabled:opacity-50">
                    Cancel
                  </button>
                  <button
                    type="button"
                    onClick={handleSave}
                    disabled={busy}
                    className="flex min-w-44 items-center justify-center gap-2 rounded-2xl bg-brown-coffee px-6 py-3.5 text-base font-extrabold text-white hover:bg-light-brown-coffee disabled:opacity-60"
                  >
                    {saving && <span className="h-4 w-4 animate-spin rounded-full border-2 border-white/40 border-t-white" />}
                    {isEdit ? "Save changes" : "Create class"}
                  </button>
                </div>
              </>
            )}
          </DialogPanel>
        </div>
      </div>
    </Dialog>
  );
}
